/**
 * Reflection components for profile items
 * Simplified: Reflections are created by profile owners, others comment via Comments
 */
import React, { useState, useEffect, useRef } from 'react';
import SlateEditor from './SlateEditor';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Avatar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  RateReview as ReflectionIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Comment as CommentIcon,
  AddReaction as AddReactionIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { Comments as ReflectionComments } from 'components/common/Comments';
import { formatDistanceToNow } from 'date-fns';

// Available emoji reactions
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👏'];

/**
 * Emoji Reaction Button Component
 */
function LikeButton({ contentType, objectId, currentUser, size = 'small' }) {
  const [reactions, setReactions] = useState({});  // { emoji: count }
  const [myReaction, setMyReaction] = useState(null);  // User's current reaction emoji
  const [myLikeId, setMyLikeId] = useState(null);  // ID of user's like for deletion
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    fetchReactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchReactions = async () => {
    try {
      const response = await axiosService.get('/interact/likes/', {
        params: {
          content_type: contentType,
          object_id: objectId,
        }
      });
      const likes = response.data.results || response.data || [];
      
      // Count reactions by emoji
      const reactionCounts = {};
      let userReaction = null;
      let userLikeId = null;
      
      likes.forEach(like => {
        const emoji = like.reaction_type || '👍';
        reactionCounts[emoji] = (reactionCounts[emoji] || 0) + 1;
        
        if (String(like.user) === String(currentUser?.id)) {
          userReaction = emoji;
          userLikeId = like.id;
        }
      });
      
      setReactions(reactionCounts);
      setMyReaction(userReaction);
      setMyLikeId(userLikeId);
    } catch (err) {
      console.error('Failed to fetch reactions:', err);
    }
  };

  const handleReaction = async (emoji) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setShowPicker(false);
      
      // If clicking the same emoji, remove the reaction
      if (myReaction === emoji && myLikeId) {
        await axiosService.delete(`/interact/likes/${myLikeId}/`);
        setReactions(prev => {
          const updated = { ...prev };
          updated[emoji] = Math.max(0, (updated[emoji] || 1) - 1);
          if (updated[emoji] === 0) delete updated[emoji];
          return updated;
        });
        setMyReaction(null);
        setMyLikeId(null);
      } else {
        // If user has existing reaction, remove it first
        if (myReaction && myLikeId) {
          await axiosService.delete(`/interact/likes/${myLikeId}/`);
          setReactions(prev => {
            const updated = { ...prev };
            updated[myReaction] = Math.max(0, (updated[myReaction] || 1) - 1);
            if (updated[myReaction] === 0) delete updated[myReaction];
            return updated;
          });
        }
        
        // Create new reaction
        const response = await axiosService.post('/interact/likes/', {
          content_type: contentType,
          object_id: objectId,
          reaction_type: emoji,
        });
        
        setReactions(prev => ({
          ...prev,
          [emoji]: (prev[emoji] || 0) + 1
        }));
        setMyReaction(emoji);
        setMyLikeId(response.data.id);
      }
    } catch (err) {
      console.error('Failed to toggle reaction:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }} ref={pickerRef}>
      {/* Display existing reactions */}
      {Object.entries(reactions).map(([emoji, count]) => (
        <Chip
          key={emoji}
          label={`${emoji} ${count}`}
          size="small"
          onClick={() => handleReaction(emoji)}
          disabled={loading || !currentUser}
          variant={myReaction === emoji ? 'filled' : 'outlined'}
          color={myReaction === emoji ? 'primary' : 'default'}
          sx={{ 
            cursor: 'pointer',
            fontSize: '0.9rem',
            height: 24,
            '& .MuiChip-label': { px: 1 }
          }}
        />
      ))}
      
      {/* Add reaction button */}
      <IconButton
        size={size}
        onClick={() => setShowPicker(!showPicker)}
        disabled={loading || !currentUser}
        sx={{ p: 0.5 }}
        aria-label="Add reaction"
      >
        <AddReactionIcon fontSize={size} />
      </IconButton>
      
      {/* Emoji picker popup */}
      {showPicker && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            mb: 0.5,
            p: 0.5,
            display: 'flex',
            gap: 0.5,
            zIndex: 1000,
            borderRadius: 2,
          }}
        >
          {REACTION_EMOJIS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => handleReaction(emoji)}
              aria-label={`React with ${emoji}`}
              sx={{
                fontSize: '1.2rem',
                p: 0.5,
                backgroundColor: myReaction === emoji ? 'primary.light' : 'transparent',
                '&:hover': { transform: 'scale(1.2)' },
                transition: 'transform 0.1s',
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Paper>
      )}
    </Box>
  );
}

/**
 * Component to display a single reflection
 */
export function ReflectionCard({ reflection, currentUser, onEdit, onDelete, onRefresh }) {
  const [commentCount, setCommentCount] = useState(0);
  
  // author is returned as UUID string directly, not as an object
  const authorId = reflection.author_detail?.id || reflection.author;
  const isAuthor = currentUser?.id && authorId && String(authorId) === String(currentUser.id);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Avatar src={reflection.author_detail?.avatar} alt={`${reflection.author_detail?.first_name || ''} ${reflection.author_detail?.last_name || ''}`} sx={{ width: 48, height: 48 }}>
            {reflection.author_detail?.first_name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.5}>
                  {reflection.author_detail?.first_name} {reflection.author_detail?.last_name}
                </Typography>
                {reflection.title && (
                  <Typography variant="subtitle2" color="primary" gutterBottom mt={0.5} sx={{ fontWeight: 500 }}>
                    {reflection.title}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: .5 }}>
                  {formatDistanceToNow(new Date(reflection.created), { addSuffix: true })}
                </Typography>
              </Box>
              {/* Delete button for author */}
              {isAuthor && onDelete && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(reflection.id)}
                  aria-label="Delete reflection"
                  title="Delete reflection"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Reflection content */}
            {reflection.media_type === 'text' && reflection.content && (
              <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {reflection.content}
              </Typography>
            )}
            {reflection.media_type === 'audio' && reflection.audio_file && (
              <audio controls src={reflection.audio_file} style={{ width: '100%', marginTop: 8 }} />
            )}
            {reflection.media_type === 'video' && reflection.video_file && (
              <video controls src={reflection.video_file} style={{ width: '100%', maxHeight: 300, marginTop: 8 }} />
            )}
            
            {/* Like button and comment count row */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <LikeButton contentType="reflection" objectId={reflection.id} currentUser={currentUser} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CommentIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {commentCount}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Comments Section */}
        <ReflectionComments 
          targetId={reflection.id} 
          targetType="reflection"
          currentUser={currentUser} 
          onCommentCountChange={setCommentCount}
          reflection={reflection} 
        />
      </CardContent>
    </Card>
  );
}

/**
 * Dialog for adding/editing a reflection
 */
export function ReflectionDialog({ open, onClose, reflection, contentType, objectId }) {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (reflection) {
      setFormData({
        title: reflection.title || '',
        content: reflection.content || '',
      });
    } else {
      setFormData({ title: '', content: '' });
    }
    setError(null);
  }, [reflection, open]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const isEditing = !!reflection;
      const endpoint = isEditing ? `/reflections/${reflection.id}/` : '/reflections/';
      const method = isEditing ? 'patch' : 'post';

      const payload = { ...formData, media_type: 'text' };
      if (!isEditing && contentType && objectId) {
        payload.content_type_name = contentType;
        payload.object_id = objectId;
      }
      await axiosService[method](endpoint, payload);

      onClose(true);
      setFormData({ title: '', content: '' });
    } catch (err) {
      console.error('Failed to save reflection:', err);
      setError(err.response?.data?.error || 'Failed to save reflection');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{reflection ? 'Edit Reflection' : 'Add Reflection'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Title (optional)"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., 'My thoughts on this experience'"
            fullWidth
          />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Share your reflection (rich text supported)
            </Typography>
            <SlateEditor
              value={formData.content}
              onChange={(val) => setFormData({ ...formData, content: val || '' })}
              height={300}
              placeholder="Share your thoughts, insights, or experiences..."
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !formData.content.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {reflection ? 'Save' : 'Add Reflection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Component to display reflections list for a profile item
 */
export function ReflectionsList({ contentType, objectId, isOwner, currentUser }) {
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingReflection, setEditingReflection] = useState(null);
  const [error, setError] = useState(null);

  const fetchReflections = async () => {
    try {
      setLoading(true);
      const response = await axiosService.get('/reflections/for-item/', {
        params: { content_type: contentType, object_id: objectId }
      });
      setReflections(response.data);
    } catch (error) {
      console.error('Failed to fetch reflections:', error);
      setError('Failed to load reflections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReflections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, objectId]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reflection?')) {
      return;
    }
    try {
      await axiosService.delete(`/reflections/${id}/`);
      fetchReflections();
    } catch (error) {
      console.error('Failed to delete reflection:', error);
      setError(error.response?.data?.error || 'Failed to delete reflection');
    }
  };

  const handleDialogClose = (success) => {
    setShowDialog(false);
    setEditingReflection(null);
    if (success) {
      fetchReflections();
    }
  };

  const handleEdit = (reflection) => {
    setEditingReflection(reflection);
    setShowDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Reflections</Typography>
        {isOwner && (
          <Button
            startIcon={<ReflectionIcon />}
            variant="outlined"
            size="small"
            onClick={() => setShowDialog(true)}
          >
            Add Reflection
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {reflections.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No reflections yet. {isOwner && 'Share your thoughts on this experience!'}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {reflections.map((reflection) => (
            <ReflectionCard
              key={reflection.id}
              reflection={reflection}
              currentUser={currentUser}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onRefresh={fetchReflections}
            />
          ))}
        </Stack>
      )}

      <ReflectionDialog
        open={showDialog}
        onClose={handleDialogClose}
        reflection={editingReflection}
        contentType={contentType}
        objectId={objectId}
      />
    </Box>
  );
}

// Alias for backwards compatibility
export const AddReflectionDialog = ReflectionDialog;
